import React from "react";
import { useI18n } from "@/i18n";

export const countryLabel = (c, lang) => {
  if (!c) return "";
  if (lang === "ar") return c.name_ar || c.name_en || c.name;
  if (lang === "en") return c.name_en || c.name;
  return c.name || c.name_en;
};

export const provinceLabel = (p, lang) => {
  if (!p) return "";
  if (typeof p === "string") return p;
  if (lang === "ar") return p.name_ar || p.name_en || p.name;
  if (lang === "en") return p.name_en || p.name;
  return p.name || p.name_en;
};

// Matches a stored value against any localized spelling or ISO code
export const findCountry = (countries, value) => {
  if (!value) return null;
  const v = String(value).trim().toLowerCase();
  return (countries || []).find((c) =>
    [c.name, c.name_en, c.name_ar, c.code].filter(Boolean).some((x) => String(x).toLowerCase() === v)
  ) || null;
};

/** Country <select>. Displays the localized name, always stores the canonical `name`. */
export function CountrySelect({
  countries = [], value = "", onChange, testid, required = false,
  className = "input", placeholder,
}) {
  const { lang, t } = useI18n();
  const sorted = [...countries].sort((a, b) =>
    countryLabel(a, lang).localeCompare(countryLabel(b, lang), lang === "ar" ? "ar" : lang)
  );
  const matched = findCountry(countries, value);
  return (
    <select
      required={required}
      data-testid={testid}
      value={matched ? matched.name : value}
      onChange={(e) => onChange(e.target.value)}
      className={className}
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      <option value="">— {placeholder || t("select_option")} —</option>
      {value && !matched && <option value={value}>{value}</option>}
      {sorted.map((c) => (
        <option key={c.code} value={c.name}>{countryLabel(c, lang)} ({c.code})</option>
      ))}
    </select>
  );
}

/** Province selector driven by the selected country. Falls back to a free text input. */
export function ProvinceSelect({
  countries = [], country = "", value = "", onChange, testid, required = false,
  className = "input",
}) {
  const { lang, t } = useI18n();
  const c = findCountry(countries, country);
  const provinces = c?.provinces || [];
  if (!provinces.length) {
    return (
      <input
        required={required}
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={className}
      />
    );
  }
  const known = provinces.some((p) => p.name === value);
  return (
    <select
      required={required}
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={className}
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      <option value="">— {t("select_option")} —</option>
      {value && !known && <option value={value}>{value}</option>}
      {provinces.map((p) => (
        <option key={p.name} value={p.name}>{provinceLabel(p, lang)}</option>
      ))}
    </select>
  );
}
